import React, { useState, useRef, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift, size,

    useClick,
    useDismiss,
    useRole,
    useInteractions, FloatingFocusManager
} from '@floating-ui/react';
import { IsMobileContext, FormPanelContext } from "../../context/contexts";

const closeIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
</svg>;
// Shown in the mobile "more below" affordance - the calendar stacks its months
// vertically, so this tells users to scroll down rather than swipe across.
const downChevron = <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
</svg>;

// The field as in the redesign (Olympic.DesignFramework): a label above an oly-form-input with the picker's icon inside it.
const FIELD_LABEL = "oly-form-label whitespace-nowrap max-md:text-base max-md:font-bold max-md:text-ink max-md:mb-2";
const FIELD_INPUT = "oly-form-input md:pl-[2.625rem] max-md:pr-11 text-left truncate cursor-pointer placeholder:text-ink-soft max-md:py-4 max-md:text-base max-md:border-ink/60 max-md:placeholder:text-ink";
// the label, with an optional shorter wording on phones
const fieldLabel = (label, shortLabel) => shortLabel
    ? <span className={FIELD_LABEL}><span className="md:hidden">{shortLabel}</span><span className="max-md:hidden">{label}</span></span>
    : <span className={FIELD_LABEL}>{label}</span>;
// the icons' shapes run to the edge of their viewBox: overflow-visible so Safari, at the fractional positions the field
// puts them on, does not trim the last pixel column (the disabled Destination and Dates icons looked cut off)
const FIELD_ICON = "oly-field-icon absolute md:left-3.5 max-md:right-3.5 top-1/2 -translate-y-1/2 pointer-events-none flex [&_svg]:size-5 [&_svg]:overflow-visible";

// anchor: where the desktop panel sits and how wide it is -
//   undefined  below the field, at least 400px wide (688px with doubleWidth)
//   "field"    below the field, exactly as wide as it (the island hop type-ahead)
//   "form"     below the whole search form, as wide as it (the airport panel)
const PopoverInput = ({ label, shortLabel, fieldClass = "", icon, children, inputText, isLoading, onTextUpdate, filterText, isOpen, setIsOpen, disabled, footer, header, panelMaxHeight = 450, panelWidthClass = "", doubleWidth, error, anchor }) => {
    const scrollPaneRef = useRef(null);
    const panelRef = useRef(null);
    const wrapRef = useRef(null);
    const isMobile = useContext(IsMobileContext);
    const formPanelRef = useContext(FormPanelContext);
    // Whether the mobile sheet has content scrolled out of view below the fold.
    const [canScrollDown, setCanScrollDown] = useState(false);

    // The popover lives inside the widget's shadow root, so its z-index can
    // never escape whatever stacking context the host page wraps the widget
    // in (e.g. olympicholidays.com wraps it at z-index 80, and its island-
    // hopping banner artwork sits at 999 and painted over the open panels).
    // While a panel is open, lift every capping host ancestor above typical
    // page content, and restore the original values on close. 1090 clears
    // page artwork but stays below the site's header/modal layer (9999).
    useEffect(() => {
        if (!isOpen) return;
        const bumped = [];
        let el = wrapRef.current?.getRootNode()?.host?.parentElement;
        while (el && el !== document.body) {
            // an ancestor with its own z-index, or one that isolates its children (the Hyperion hero is `isolate`)
            const style = getComputedStyle(el);
            if (style.zIndex !== "auto" || style.isolation === "isolate") {
                bumped.push([el, el.style.zIndex]);
                el.style.zIndex = "1090";
            }
            el = el.parentElement;
        }
        return () => {
            for (const [node, prev] of bumped) {
                node.style.zIndex = prev;
            }
        };
    }, [isOpen]);

    // The desktop panel opens below its field (or the form) and is only as tall as the window leaves it: with the form
    // low on the screen (the hero's search form on a laptop) it was squashed to its title. Scroll the page up just
    // enough for the whole panel, never so far that the field leaves the top of the window.
    useEffect(() => {
        if (!isOpen || isMobile) return;
        const frame = requestAnimationFrame(() => {
            const reference = (anchor == "form" && formPanelRef?.current) || wrapRef.current;
            const panel = panelRef.current, pane = scrollPaneRef.current;
            if (!reference || !panel || !pane) return;
            // the panel's height uncapped (its list unscrolled), at most panelMaxHeight
            const height = Math.min(panelMaxHeight, panel.offsetHeight - pane.clientHeight + pane.scrollHeight);
            const rect = reference.getBoundingClientRect();
            const shortBy = height + 8 + 25 - (window.innerHeight - rect.bottom); // + its offset and the gap size() keeps below it
            const scroll = Math.min(shortBy, rect.top - 16);
            if (scroll > 0) {
                window.scrollBy({ top: scroll, behavior: "smooth" });
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [isOpen, isMobile, anchor, formPanelRef, panelMaxHeight]);

    // Prevent body scroll when a mobile bottom-sheet is open
    useEffect(() => {
        if (isMobile && isOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isMobile, isOpen]);

    // Track whether there's more to scroll so the mobile sheet can hint at it.
    // The calendar in particular stacks its months vertically, so without a cue
    // users assume the first month is all there is (or try to swipe sideways).
    // A ResizeObserver covers async content (e.g. dates loading in after open).
    useEffect(() => {
        if (!isMobile || !isOpen) {
            setCanScrollDown(false);
            return;
        }
        const el = scrollPaneRef.current;
        if (!el) return;
        const update = () => {
            setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
        };
        update();
        el.addEventListener("scroll", update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            el.removeEventListener("scroll", update);
            ro.disconnect();
        };
    }, [isMobile, isOpen]);

    const floatingOpts = {
        open: isOpen,
        onOpenChange: (a, event, reason) => {
            if (reason == "click") {
                if (a) {
                    setIsOpen(true);
                }
            } else {
                setIsOpen(a);
            }
        },
        whileElementsMounted: autoUpdate,
    };

    if (isMobile) {
        delete scrollPaneRef?.current?.style?.maxHeight;
        floatingOpts.strategy = "fixed";
        floatingOpts.middleware = [{
            name: "originMiddleware",
            fn({ x, y }) {
                return {
                    x: 0,
                    y: 0,
                    reset: true,
                };
            },
        }];
    } else {
        // Fixed, not absolute: over a banner the form sits inside the hero,
        // which clips its overflow - an absolute panel was cut off at the
        // hero's bottom edge (and sized to the little space left inside it).
        floatingOpts.strategy = "fixed";

        floatingOpts.placement = "bottom-start";
        floatingOpts.middleware = [offset(8), shift({ padding: 8 }), size({
            apply({ availableHeight, elements, rects }) {
                if (anchor) {
                    elements.floating.style.width = `${rects.reference.width}px`;
                }
                if (availableHeight > panelMaxHeight) {
                    availableHeight = panelMaxHeight;
                }
                // Cap the whole panel (not just the scroll pane) so the pinned
                // footer is always within the viewport - the scroll pane flexes
                // to make room for it.
                const value = `${Math.max(0, availableHeight - 25)}px`;
                if (panelRef.current) {
                    panelRef.current.style.maxHeight = value;
                }
            },
        })];
    }
    const { refs, floatingStyles, context } = useFloating(floatingOpts);

    // The airport panel spans the whole form: position (and size) it against the form panel, not the field.
    useEffect(() => {
        if (anchor == "form" && formPanelRef?.current) {
            refs.setPositionReference(formPanelRef.current);
        }
    }, [anchor, formPanelRef, refs]);

    const mobileOverlayStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    };

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role,
    ]);


    var textboxValue = filterText ?? "";
    var textboxPlaceholder = inputText;

    if (disabled || isLoading) {
        // A disabled field shows its placeholder even while its list is still unknown.
        const showLoading = isLoading && !disabled;
        return (<div className={"relative w-full min-w-0 flex-1 " + fieldClass}>
            {label && fieldLabel(label, shortLabel)}
            <div className="relative">
                <input type="text" autoComplete="off" disabled={disabled} readOnly className={FIELD_INPUT + " disabled:cursor-not-allowed disabled:opacity-60"}
                    placeholder={showLoading ? "Loading..." : textboxPlaceholder} value={showLoading ? "" : textboxValue} aria-label={label} />
                <span className={FIELD_ICON + " text-navy-700 opacity-60"}>{icon}</span>
            </div>
        </div>);
    }
    let isReadOnly = true;
    if (onTextUpdate) {
        isReadOnly = false;
    } else {
        onTextUpdate = (t) => { }
    }

    // Minimum desktop popover width. NOTE: in Tailwind v4 the spacing scale is
    // dynamic, so min-w-172 (43rem/688px) and min-w-100 (25rem/400px) are real
    // widths, not no-ops - without them the popovers collapse to content width
    // and look squished. Mobile is a full-width sheet, so no min width.
    let minWidth = anchor ? "" : doubleWidth ? "min-w-172" : "min-w-100";
    if (isMobile) {
        minWidth = "";
    }

    // The mobile panel covers the screen (position: fixed), but over a banner the form sits on a frosted card whose
    // backdrop-filter makes it the containing block for fixed descendants - the panel was trapped inside the card.
    // So on mobile the panel is rendered at the top of the widget's shadow root instead.
    const portal = (node) => isMobile && wrapRef.current ? createPortal(node, wrapRef.current.getRootNode()) : node;

    const panelTitle = (
        <div className="flex items-start justify-between gap-4 shrink-0">
            <p className="oly-form-label text-sm max-md:text-base mb-0">{label}</p>
            <button type="button" aria-label="Close" onClick={() => setIsOpen(false)}
                className="text-ink-soft hover:text-navy-700 cursor-pointer -mt-0.5">
                {closeIcon}
            </button>
        </div>
    );

    return (
        <div ref={wrapRef} className={"relative w-full min-w-0 flex-1 z-auto " + fieldClass}>
            {label && fieldLabel(label, shortLabel)}
            <div className="relative">
                <input readOnly={isReadOnly} type="text" autoComplete="off" ref={refs.setReference} {...getReferenceProps()} value={textboxValue} title={isReadOnly ? textboxValue : undefined} onChange={(e) => onTextUpdate(e.target.value)} aria-invalid={error ? true : undefined} aria-label={label}
                    className={FIELD_INPUT + (error ? " !border-error ring-1 ring-error" : "")} placeholder={textboxPlaceholder} />
                <span className={FIELD_ICON + " " + (error ? "text-error" : "text-navy-700")}>
                    {icon}
                </span>
            </div>
            {/* Validation hint, absolutely positioned so it can't shift the
                horizontal field row. */}
            {error && (
                <span role="alert" className="absolute left-0 top-full mt-1 text-xs text-error whitespace-nowrap z-20">
                    {error}
                </span>
            )}
            {isOpen && portal(
                <FloatingFocusManager context={context} modal={isMobile} initialFocus={isMobile ? -1 : refs.reference}>
                    {isMobile ? (
                        // Mobile: a full-screen panel, as in the mockup
                        <div
                            ref={refs.setFloating}
                            style={mobileOverlayStyles}
                            {...getFloatingProps()}
                            className="z-50 bg-white text-ink flex flex-col"
                        >
                            <div className="px-5 pt-5 pb-3 shrink-0 space-y-3">
                                {panelTitle}
                                {header}
                            </div>
                            <div className="relative flex-1 min-h-0 flex flex-col">
                                <div ref={scrollPaneRef} className="overflow-y-auto flex-1 px-5 pb-3"
                                    onClickCapture={(e) => {
                                        // Dismiss the on-screen keyboard when a row is
                                        // tapped - with the keyboard up it covers the
                                        // footer, so Done looks like it vanished.
                                        // getRootNode() because we render in a shadow DOM.
                                        const active = e.currentTarget.getRootNode()?.activeElement;
                                        if (active && active.tagName == "INPUT" && active.type == "text" && !e.target.closest("input")) {
                                            active.blur();
                                        }
                                    }}>
                                    {children}
                                </div>
                                {/* "More below" hint: a fade and a chevron, shown only
                                    while there is content below the fold, so it points
                                    down (scroll) rather than across. */}
                                {canScrollDown && (
                                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 flex items-end justify-center pb-1 text-ink-soft"
                                        style={{ background: "linear-gradient(to top, #ffffff 35%, rgba(255,255,255,0))" }}>
                                        {downChevron}
                                    </div>
                                )}
                            </div>
                            {footer != null && (
                                <div className="px-5 py-4 border-t border-sky-100 shrink-0">
                                    {footer}
                                </div>
                            )}
                        </div>
                    ) : (
                        // Desktop: a card below the field (or the form), as in the mockup
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}
                            className={"z-[9999] " + minWidth}
                        >
                            <div ref={panelRef} className={"bg-white text-ink rounded-2xl p-5 shadow-card border border-sky-100/70 flex flex-col gap-3 overflow-hidden " + panelWidthClass}>
                                {panelTitle}
                                {header != null && (
                                    <div className="shrink-0">
                                        {header}
                                    </div>
                                )}
                                <div ref={scrollPaneRef} className="overflow-y-auto flex-1 min-h-0 -mx-1 px-1">
                                    {children}
                                </div>
                                {footer != null && (
                                    <div className="shrink-0 pt-1">
                                        {footer}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </FloatingFocusManager>
            )}
        </div>);
}
export default PopoverInput;
